import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGoogle, FaTimes, FaCheckCircle, FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';
import axios from 'axios';
import OTPInput from './OTPInput';
import api, { getApiUrl, getGoogleOAuthStartUrl } from '../config/api';
import workspaceAPI from '../services/workspaceAPI';
import { registerPushNotifications } from '../services/pushNotificationService';
import {
  clearCompanyStaffSession,
  saveCompanyStaffSession,
} from '../utils/companyStaffSession';
import { syncWorkspaceAccessFromResponse } from '../utils/workspaceSession';
import './LoginModal.css';

const COMPANY_HOST_ROLES = new Set(['host', 'company_admin', 'company_manager', 'company_staff']);

const blankErrors = {
  email: '',
  fullName: '',
  phoneNumber: '',
  otp: '',
  workspaceJoinCode: '',
  workspaceJoinName: '',
  workspaceJoinConfirmPassword: '',
  companyLoginEmail: '',
  companyLoginPassword: '',
  companyName: '',
  companyEmail: '',
  companyContactNumber: '',
  companyAddress: '',
  companyPassword: '',
  companyConfirmPassword: '',
  companyAadhaar: '',
  aadhaarOtp: '',
};

const formatAadhaarInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 12);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
};

const getAadhaarDigits = (value) => String(value || '').replace(/\D/g, '');

const LoginModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [successNote, setSuccessNote] = useState('');
  const [fieldErrors, setFieldErrors] = useState(blankErrors);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [step, setStep] = useState('email');
  const [isNewUser, setIsNewUser] = useState(null);
  const [checkingUser, setCheckingUser] = useState(false);

  const [authMode, setAuthMode] = useState('otp'); // otp | company-login | company-register
  const [companyLoginEmail, setCompanyLoginEmail] = useState('');
  const [companyLoginPassword, setCompanyLoginPassword] = useState('');
  const [companyLoginMode, setCompanyLoginMode] = useState('login');
  const [workspaceJoinCode, setWorkspaceJoinCode] = useState('');
  const [workspaceJoinName, setWorkspaceJoinName] = useState('');
  const [workspaceJoinConfirmPassword, setWorkspaceJoinConfirmPassword] = useState('');
  const [workspaceJoinCompanyName, setWorkspaceJoinCompanyName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyContactNumber, setCompanyContactNumber] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyPassword, setCompanyPassword] = useState('');
  const [companyConfirmPassword, setCompanyConfirmPassword] = useState('');
  const [companyAadhaar, setCompanyAadhaar] = useState('');
  const [aadhaarOtp, setAadhaarOtp] = useState('');
  const [aadhaarVerified, setAadhaarVerified] = useState(false);
  const [aadhaarVerificationToken, setAadhaarVerificationToken] = useState('');
  const [aadhaarVerifiedName, setAadhaarVerifiedName] = useState('');
  const [aadhaarVerifiedPhoto, setAadhaarVerifiedPhoto] = useState('');
  const [aadhaarMaskedMobile, setAadhaarMaskedMobile] = useState('');
  const [aadhaarOtpSent, setAadhaarOtpSent] = useState(false);
  const [aadhaarOtpSending, setAadhaarOtpSending] = useState(false);
  const [aadhaarVerifying, setAadhaarVerifying] = useState(false);
  const [aadhaarConfig, setAadhaarConfig] = useState({
    configured: false,
    provider: 'local',
    requiresContactForOtp: true,
  });
  const [visiblePasswords, setVisiblePasswords] = useState({});

  const togglePasswordVisibility = (key) => {
    setVisiblePasswords((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const validateEmail = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalized)) return 'Please enter a valid email address';
    return '';
  };

  const validatePhone = (value, label = 'Phone number') => {
    const normalized = String(value || '').trim();
    if (!normalized) return `${label} is required`;
    const digitsOnly = normalized.replace(/[^\d]/g, '');
    if (digitsOnly.length < 10) return `Please enter a valid ${label.toLowerCase()}`;
    return '';
  };

  const validateOtp = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized) return 'OTP is required';
    if (normalized.length !== 6) return 'OTP must be 6 digits';
    return '';
  };

  const clearAllLocalErrors = () => {
    setError('');
    setSuccessNote('');
    setFieldErrors(blankErrors);
  };

  const resetModalState = () => {
    setEmail('');
    setFullName('');
    setPhoneNumber('');
    setOtp('');
    setLoading(false);
    setShowSuccess(false);
    setStep('email');
    setIsNewUser(null);
    setCheckingUser(false);
    setAuthMode('otp');
    setCompanyLoginEmail('');
    setCompanyLoginPassword('');
    setCompanyLoginMode('login');
    setWorkspaceJoinCode('');
    setWorkspaceJoinName('');
    setWorkspaceJoinConfirmPassword('');
    setWorkspaceJoinCompanyName('');
    setCompanyName('');
    setCompanyEmail('');
    setCompanyContactNumber('');
    setCompanyAddress('');
    setCompanyPassword('');
    setCompanyConfirmPassword('');
    setCompanyAadhaar('');
    setAadhaarOtp('');
    setAadhaarVerified(false);
    setAadhaarVerificationToken('');
    setAadhaarVerifiedName('');
    setAadhaarVerifiedPhoto('');
    setAadhaarMaskedMobile('');
    setAadhaarOtpSent(false);
    setAadhaarOtpSending(false);
    setAadhaarVerifying(false);
    setVisiblePasswords({});
    clearAllLocalErrors();
  };

  const resetAadhaarVerification = () => {
    setAadhaarVerified(false);
    setAadhaarVerificationToken('');
    setAadhaarVerifiedName('');
    setAadhaarVerifiedPhoto('');
    setAadhaarMaskedMobile('');
    setAadhaarOtpSent(false);
    setAadhaarOtp('');
  };

  useEffect(() => {
    if (!isOpen || authMode !== 'company-register') return undefined;

    let cancelled = false;
    axios
      .get(api.auth.aadhaarConfig())
      .then((res) => {
        if (cancelled || !res.data?.success) return;
        setAadhaarConfig({
          configured: Boolean(res.data.configured),
          provider: res.data.provider || 'local',
          requiresContactForOtp: res.data.requiresContactForOtp !== false,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setAadhaarConfig({
            configured: false,
            provider: 'local',
            requiresContactForOtp: true,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, authMode]);

  useEffect(() => {
    if (!isOpen) {
      resetModalState();
      return;
    }

    const storedRedirect = localStorage.getItem('authRedirectPath');
    if (!storedRedirect) {
      const currentPath = window.location.pathname;
      if (currentPath === '/host') {
        localStorage.setItem('authRedirectPath', '/host');
      } else if (
        currentPath === '/meeting-book' ||
        currentPath === '/meeting-home' ||
        currentPath.startsWith('/meetings/')
      ) {
        localStorage.setItem('authRedirectPath', currentPath);
      } else {
        localStorage.setItem('authRedirectPath', '/');
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    const detail = urlParams.get('detail') || '';
    if (errorParam === 'unauthorized') {
      setError('Your email is not authorized to access this system. Please contact administrator.');
    } else if (errorParam === 'server_error') {
      setError('Server error occurred. Please try again.');
    } else if (errorParam === 'google_not_configured') {
      setError('Google sign-in is not configured. Please use email + OTP to log in.');
    } else if (errorParam === 'google_failed') {
      const isRedirectMismatch = detail === 'redirect_uri_mismatch' || (detail && detail.includes('redirect_uri'));
      setError(
        isRedirectMismatch
          ? 'Google sign-in failed: redirect URI mismatch. Add this exact URL in Google Cloud Console -> APIs & Services -> Credentials -> your OAuth client -> Authorized redirect URIs: http://localhost:5002/api/auth/google/callback (then Save and try again).'
          : `Google sign-in failed. ${detail ? decodeURIComponent(detail) : 'Please try again or use Email + OTP.'}`
      );
    } else if (errorParam === 'no_token') {
      setError('Login session expired or invalid. Please try again.');
    }
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      const storedRedirect = localStorage.getItem('authRedirectPath');
      let redirectPath = '/';

      if (storedRedirect) {
        redirectPath = storedRedirect;
      } else {
        const currentPath = window.location.pathname;
        if (currentPath === '/host') {
          redirectPath = '/host';
        } else if (
          currentPath === '/meeting-book' ||
          currentPath === '/meeting-home' ||
          currentPath.startsWith('/meetings/')
        ) {
          redirectPath = currentPath;
        }
      }

      localStorage.setItem('authRedirectPath', redirectPath);
      window.location.href = getGoogleOAuthStartUrl('/auth/callback');
    } catch {
      setError('Failed to initiate Google Sign-In. Please try again.');
      setLoading(false);
    }
  };

  const completeLoginSuccess = async (user, token, options = {}) => {
    const staffProfile = options.staffProfile || null;
    const mergedUser = staffProfile ? { ...user, staffProfile } : user;

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(mergedUser));
    if (staffProfile) {
      saveCompanyStaffSession(staffProfile);
    } else {
      clearCompanyStaffSession();
    }
    syncWorkspaceAccessFromResponse({
      user: mergedUser,
      workspaceAccess: options.workspaceAccess,
    });

    window.dispatchEvent(new CustomEvent('codex-authenticated'));
    await registerPushNotifications({ requestPermission: true }).catch(() => {});
    setShowSuccess(true);

    setTimeout(() => {
      onClose();
      const storedRedirect = localStorage.getItem('authRedirectPath');
      const currentPath = window.location.pathname;
      const userRole = user?.role;
      const hasCompany = Boolean(user?.company?._id || user?.company);
      let redirectPath = '/';

      if (options.redirectTo) {
        redirectPath = options.redirectTo;
        localStorage.removeItem('authRedirectPath');
      } else if (storedRedirect) {
        redirectPath = storedRedirect;
        localStorage.removeItem('authRedirectPath');
      } else if (COMPANY_HOST_ROLES.has(String(userRole || '')) || hasCompany) {
        redirectPath = '/workspace';
      } else if (currentPath === '/host') {
        redirectPath = '/host';
      } else if (
        currentPath === '/meeting-book' ||
        currentPath === '/meeting-home' ||
        currentPath.startsWith('/meetings/')
      ) {
        redirectPath = currentPath;
      }

      navigate(redirectPath);
      window.location.reload();
    }, 1400);
  };

  const handleCheckUser = async () => {
    const emailError = validateEmail(email);
    if (emailError) {
      setFieldErrors((prev) => ({ ...prev, email: emailError }));
      return;
    }

    setCheckingUser(true);
    setError('');
    try {
      const response = await axios.post(getApiUrl('/api/auth/check-user'), { email: email.trim() });
      if (response.data.success) {
        setIsNewUser(response.data.isNewUser);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError(err.response.data.message || 'This email is not authorized to access the system.');
      } else {
        setError('Failed to verify email. Please try again.');
      }
    } finally {
      setCheckingUser(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    const emailError = validateEmail(email);
    if (emailError) {
      setFieldErrors((prev) => ({ ...prev, email: emailError }));
      return;
    }

    if (isNewUser) {
      const nameError = fullName.trim().length < 2 ? 'Full name must be at least 2 characters' : '';
      const phoneError = validatePhone(phoneNumber);
      if (nameError || phoneError) {
        setFieldErrors((prev) => ({
          ...prev,
          fullName: nameError,
          phoneNumber: phoneError,
        }));
        return;
      }
    }

    setLoading(true);
    try {
      const payload = { email: email.trim() };
      if (isNewUser) {
        payload.fullName = fullName.trim();
        payload.phoneNumber = phoneNumber.trim();
      }

      const response = await axios.post(getApiUrl('/api/auth/send-otp'), payload);
      if (response.data.success) {
        setStep('otp');
        setFieldErrors(blankErrors);
      } else {
        setError(response.data.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      if (err.code === 'ERR_NETWORK' || String(err.message || '').includes('Network Error')) {
        setError('Cannot connect to server. Please make sure the backend server is running on port 5000.');
      } else if (err.response?.status === 403) {
        setError(err.response.data.message || 'This email is not authorized to access the system.');
      } else {
        setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    const otpError = validateOtp(otp);
    if (otpError) {
      setFieldErrors((prev) => ({ ...prev, otp: otpError }));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(getApiUrl('/api/auth/verify-otp'), {
        email: email.trim(),
        otp: otp.trim(),
      });

      if (response.data.success) {
        await completeLoginSuccess(response.data.user, response.data.token);
      } else {
        setError(response.data.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to verify OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(getApiUrl('/api/auth/resend-otp'), {
        email: email.trim(),
      });
      if (!response.data.success) {
        setError(response.data.message || 'Failed to resend OTP. Please try again.');
      }
    } catch {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateCompanyLoginForm = () => {
    const nextErrors = {};
    nextErrors.companyLoginEmail = validateEmail(companyLoginEmail);
    nextErrors.companyLoginPassword = companyLoginPassword ? '' : 'Password is required';
    setFieldErrors((prev) => ({ ...prev, ...nextErrors }));
    return !Object.values(nextErrors).some(Boolean);
  };

  const handleWorkspaceJoin = async (e) => {
    e.preventDefault();
    setError('');
    const nextErrors = {
      workspaceJoinCode:
        workspaceJoinCode.trim().length >= 6 ? '' : 'Please enter a valid workspace code',
      workspaceJoinName:
        workspaceJoinName.trim().length >= 2 ? '' : 'Please enter your full name',
      companyLoginEmail: validateEmail(companyLoginEmail),
      companyLoginPassword:
        String(companyLoginPassword || '').length >= 6 ? '' : 'Password must be at least 6 characters',
      workspaceJoinConfirmPassword:
        companyLoginPassword === workspaceJoinConfirmPassword ? '' : 'Confirm password does not match',
    };

    setFieldErrors((prev) => ({ ...prev, ...nextErrors }));
    if (Object.values(nextErrors).some(Boolean)) {
      return;
    }

    setLoading(true);
    try {
      const response = await workspaceAPI.join({
        inviteCode: workspaceJoinCode.trim(),
        email: companyLoginEmail.trim(),
        password: companyLoginPassword,
        name: workspaceJoinName.trim(),
      });
      if (response.data.success && response.data.token) {
        await completeLoginSuccess(response.data.user, response.data.token, {
          redirectTo: '/workspace',
          workspaceAccess: response.data.workspaceAccess,
        });
      } else {
        setError(response.data.message || 'Join failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to join workspace');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authMode !== 'company-login' || companyLoginMode !== 'join') return undefined;
    const code = workspaceJoinCode.trim().toUpperCase();
    if (code.length < 6) {
      setWorkspaceJoinCompanyName('');
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await workspaceAPI.getInvitePreview(code);
        if (!cancelled) {
          setWorkspaceJoinCompanyName(res.data?.companyName || '');
        }
      } catch {
        if (!cancelled) {
          setWorkspaceJoinCompanyName('');
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [authMode, companyLoginMode, workspaceJoinCode]);

  const handleCompanyPasswordSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessNote('');

    if (!validateCompanyLoginForm()) return;

    setLoading(true);
    try {
      const response = await axios.post(api.auth.companyLogin(), {
        email: companyLoginEmail.trim(),
        password: companyLoginPassword,
      });

      if (response.data.success) {
        await completeLoginSuccess(response.data.user, response.data.token, {
          redirectTo: '/workspace',
          workspaceAccess: response.data.workspaceAccess,
        });
      } else {
        setError(response.data.message || 'Sign in failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const validateCompanyRegisterForm = () => {
    const nextErrors = {};
    nextErrors.companyName = companyName.trim() ? '' : 'Company name is required';
    nextErrors.companyEmail = validateEmail(companyEmail);
    nextErrors.companyContactNumber = validatePhone(companyContactNumber, 'Contact number');
    const aadhaarDigits = getAadhaarDigits(companyAadhaar);
    nextErrors.companyAadhaar =
      aadhaarDigits.length === 12 ? '' : 'Enter valid 12-digit Aadhaar number';
    nextErrors.aadhaarOtp = aadhaarVerified ? '' : 'Verify Aadhaar with OTP';
    nextErrors.companyAddress = companyAddress.trim() ? '' : 'Registered address is required';
    nextErrors.companyPassword =
      String(companyPassword || '').length >= 6 ? '' : 'Password must be at least 6 characters';
    nextErrors.companyConfirmPassword =
      companyConfirmPassword === companyPassword ? '' : 'Confirm password does not match';

    setFieldErrors((prev) => ({ ...prev, ...nextErrors }));
    return !Object.values(nextErrors).some(Boolean);
  };

  const handleSendAadhaarOtp = async () => {
    setError('');
    setSuccessNote('');
    const aadhaarDigits = getAadhaarDigits(companyAadhaar);
    const aadhaarErr = aadhaarDigits.length === 12 ? '' : 'Enter valid 12-digit Aadhaar number';
    const contactErr = validatePhone(companyContactNumber, 'Contact number');
    const emailErr = validateEmail(companyEmail);

    setFieldErrors((prev) => ({
      ...prev,
      companyContactNumber: contactErr,
      companyEmail: emailErr,
      companyAadhaar: aadhaarErr,
    }));
    if (contactErr || emailErr || aadhaarErr) return;

    setAadhaarOtpSending(true);
    try {
      const payload = {
        aadhaarNumber: aadhaarDigits,
        mobileNumber: companyContactNumber.trim(),
        email: companyEmail.trim(),
        purpose: 'company_register',
      };

      const response = await axios.post(api.auth.aadhaarSendOtp(), payload);
      if (response.data.success !== false) {
        setAadhaarOtpSent(true);
        setAadhaarMaskedMobile(response.data.maskedMobile || '');
        setSuccessNote(response.data.message || 'OTP sent to your contact number / email');
        if (response.data.devOtp && process.env.NODE_ENV === 'development') {
          setAadhaarOtp(String(response.data.devOtp));
        }
      } else {
        setError(response.data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send Aadhaar OTP');
    } finally {
      setAadhaarOtpSending(false);
    }
  };

  const handleVerifyAadhaarOtp = async () => {
    setError('');
    const otpErr = validateOtp(aadhaarOtp);
    if (otpErr) {
      setFieldErrors((prev) => ({ ...prev, aadhaarOtp: otpErr }));
      return;
    }

    setAadhaarVerifying(true);
    try {
      const verifyPayload = {
        aadhaarNumber: getAadhaarDigits(companyAadhaar),
        otp: aadhaarOtp.trim(),
        mobileNumber: companyContactNumber.trim(),
        purpose: 'company_register',
      };

      const response = await axios.post(api.auth.aadhaarVerifyOtp(), verifyPayload);
      if (response.data.verificationToken) {
        setAadhaarVerified(true);
        setAadhaarVerificationToken(response.data.verificationToken);
        setAadhaarVerifiedName(response.data.verifiedName || '');
        setAadhaarVerifiedPhoto(response.data.verifiedPhoto || '');
        setSuccessNote(
          response.data.verifiedName
            ? `Aadhaar verified: ${response.data.verifiedName}`
            : 'Aadhaar verified successfully'
        );
        setFieldErrors((prev) => ({ ...prev, aadhaarOtp: '', companyAadhaar: '' }));
      } else {
        setError(response.data.message || 'Verification failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired OTP');
    } finally {
      setAadhaarVerifying(false);
    }
  };

  const handleCompanyRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessNote('');
    if (!validateCompanyRegisterForm()) return;

    setLoading(true);
    try {
      const response = await axios.post(api.auth.registerCompany(), {
        companyName: companyName.trim(),
        email: companyEmail.trim(),
        contactNumber: companyContactNumber.trim(),
        registeredAddress: companyAddress.trim(),
        password: companyPassword,
        confirmPassword: companyConfirmPassword,
        aadhaarNumber: getAadhaarDigits(companyAadhaar),
        aadhaarVerificationToken,
      });

      if (response.data.success && response.data.token && response.data.user) {
        await completeLoginSuccess(response.data.user, response.data.token, {
          redirectTo: '/workspace',
        });
        return;
      }

      if (response.data.success) {
        setError('Account created but sign-in failed. Please use Company Login.');
      } else {
        setError(response.data.message || 'Failed to create company account.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create company account.');
    } finally {
      setLoading(false);
    }
  };

  const goBackToEmail = () => {
    setStep('email');
    setOtp('');
    clearAllLocalErrors();
  };

  const switchToCompanyLogin = () => {
    setAuthMode('company-login');
    setStep('email');
    clearAllLocalErrors();
  };

  const switchToCompanyRegister = () => {
    setAuthMode('company-register');
    setStep('email');
    clearAllLocalErrors();
  };

  const switchToOtpLogin = () => {
    setAuthMode('otp');
    setStep('email');
    clearAllLocalErrors();
  };

  const onEmailValueChange = (value) => {
    setEmail(value);
    setError('');
    if (fieldErrors.email) {
      setFieldErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    }
  };

  const title = step === 'otp'
    ? 'Enter Verification Code'
    : authMode === 'company-register'
      ? 'Create Company Account'
      : authMode === 'company-login'
        ? 'Company Account Login'
        : 'Welcome Back';

  const subtitle = step === 'otp'
    ? `We've sent a 6-digit code to ${email}`
    : authMode === 'company-register'
      ? 'Register your company and continue in the same popup'
      : authMode === 'company-login'
        ? 'Sign in with your company email and password (not guest Google/OTP login)'
        : 'Sign in to access your account';

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="login-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="login-modal"
          initial={{ opacity: 0, x: 48 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 48 }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="login-modal-header">
            {step === 'otp' && (
              <button onClick={goBackToEmail} className="back-btn">
                <FaArrowLeft />
              </button>
            )}
            <button onClick={onClose} className="close-btn">
              <FaTimes />
            </button>
          </div>

          <div className="login-modal-content">
            <div className="login-header">
              <h1 className="login-title">{title}</h1>
              <p className="login-subtitle">{subtitle}</p>
            </div>

            {error && (
              <motion.div className="error-message" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                {error}
              </motion.div>
            )}

            {successNote && (
              <motion.div className="success-inline-note" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                {successNote}
              </motion.div>
            )}

            <motion.div className="login-form-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {authMode === 'otp' && step === 'email' && (
                <form onSubmit={handleSendOtp} className="login-form">
                  <div className="form-group">
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => {
                        onEmailValueChange(e.target.value);
                        setIsNewUser(null);
                      }}
                      onBlur={() => {
                        if (email && !validateEmail(email) && isNewUser === null) handleCheckUser();
                      }}
                      className={`form-input ${fieldErrors.email ? 'input-error' : ''}`}
                      disabled={loading || checkingUser}
                      required
                    />
                    {fieldErrors.email && <div className="field-error-message">{fieldErrors.email}</div>}
                  </div>

                  {isNewUser === null && email && !fieldErrors.email && (
                    <button type="button" className="btn-continue" onClick={handleCheckUser} disabled={checkingUser}>
                      {checkingUser ? (
                        <>
                          <span className="spinner-small"></span>
                          <span>Checking...</span>
                        </>
                      ) : (
                        'Continue'
                      )}
                    </button>
                  )}

                  {isNewUser === true && (
                    <>
                      <div className="form-group">
                        <input
                          type="text"
                          placeholder="Full Name"
                          value={fullName}
                          onChange={(e) => {
                            setFullName(e.target.value);
                            setError('');
                          }}
                          className={`form-input ${fieldErrors.fullName ? 'input-error' : ''}`}
                          disabled={loading}
                          required
                        />
                        {fieldErrors.fullName && <div className="field-error-message">{fieldErrors.fullName}</div>}
                      </div>

                      <div className="form-group">
                        <input
                          type="tel"
                          placeholder="Phone Number"
                          value={phoneNumber}
                          onChange={(e) => {
                            setPhoneNumber(e.target.value);
                            setError('');
                          }}
                          className={`form-input ${fieldErrors.phoneNumber ? 'input-error' : ''}`}
                          disabled={loading}
                          required
                        />
                        {fieldErrors.phoneNumber && <div className="field-error-message">{fieldErrors.phoneNumber}</div>}
                      </div>
                    </>
                  )}

                  {isNewUser !== null && (
                    <button
                      type="submit"
                      className="btn-verify"
                      disabled={loading || (isNewUser && (!fullName.trim() || !phoneNumber.trim()))}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-small"></span>
                          <span>Sending OTP...</span>
                        </>
                      ) : (
                        'Send Verification Code'
                      )}
                    </button>
                  )}

                  <button
                    type="button"
                    className="btn-change-email-link btn-change-email-link-bottom"
                    onClick={switchToCompanyLogin}
                    disabled={loading}
                  >
                    Sign in with password (company account)
                  </button>
                </form>
              )}

              {authMode === 'otp' && step === 'otp' && (
                <form onSubmit={handleVerifyOtp} className="login-form">
                  <div className="form-group">
                    <OTPInput
                      value={otp}
                      onChange={(value) => {
                        setOtp(value);
                        setError('');
                        if (fieldErrors.otp) {
                          setFieldErrors((prev) => ({ ...prev, otp: validateOtp(value) }));
                        }
                      }}
                      onComplete={(value) => setOtp(value)}
                      disabled={loading}
                      error={fieldErrors.otp}
                      length={6}
                    />
                    {fieldErrors.otp && <div className="field-error-message">{fieldErrors.otp}</div>}
                  </div>

                  <button type="submit" className="btn-verify" disabled={loading || otp.length !== 6}>
                    {loading ? (
                      <>
                        <span className="spinner-small"></span>
                        <span>Verifying...</span>
                      </>
                    ) : (
                      'Verify & Sign In'
                    )}
                  </button>

                  <button type="button" onClick={handleResendOtp} className="btn-resend" disabled={loading}>
                    Resend Code
                  </button>
                </form>
              )}

              {authMode === 'company-login' && (
                <form onSubmit={companyLoginMode === 'join' ? handleWorkspaceJoin : handleCompanyPasswordSignIn} className="login-form">
                  <p className="company-login-hint">
                    Company workspace login only. Guest account? Use Google or email OTP above.
                    Platform super admin → <a href="/admin">/admin</a>.
                  </p>
                  <div className="company-auth-tabs">
                    <button
                      type="button"
                      className={`company-auth-tab ${companyLoginMode === 'login' ? 'company-auth-tab--active' : ''}`}
                      onClick={() => {
                        setCompanyLoginMode('login');
                        setError('');
                        setWorkspaceJoinCompanyName('');
                      }}
                    >
                      Company login
                    </button>
                    <button
                      type="button"
                      className={`company-auth-tab ${companyLoginMode === 'join' ? 'company-auth-tab--active' : ''}`}
                      onClick={() => {
                        setCompanyLoginMode('join');
                        setError('');
                      }}
                    >
                      Join with code
                    </button>
                  </div>
                  {companyLoginMode === 'join' && (
                    <>
                      <div className="company-section-card company-section-card--join">
                        <h4>Join Workspace</h4>
                        <p>Use the invite from your company admin. Your email must be authorized first.</p>
                        {workspaceJoinCompanyName && (
                          <p className="workspace-join-company-note">
                            Joining <strong>{workspaceJoinCompanyName}</strong>
                          </p>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label" htmlFor="workspaceJoinCode">Workspace code</label>
                        <input
                          id="workspaceJoinCode"
                          type="text"
                          placeholder="Enter workspace code"
                          value={workspaceJoinCode}
                          onChange={(e) => {
                            setWorkspaceJoinCode(e.target.value.toUpperCase());
                            setError('');
                          }}
                          className={`form-input ${fieldErrors.workspaceJoinCode ? 'input-error' : ''}`}
                          maxLength={8}
                          disabled={loading}
                        />
                        {fieldErrors.workspaceJoinCode && (
                          <div className="field-error-message">{fieldErrors.workspaceJoinCode}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label" htmlFor="workspaceJoinName">Your name</label>
                        <input
                          id="workspaceJoinName"
                          type="text"
                          placeholder="Enter your full name"
                          value={workspaceJoinName}
                          onChange={(e) => {
                            setWorkspaceJoinName(e.target.value);
                            setError('');
                          }}
                          className={`form-input ${fieldErrors.workspaceJoinName ? 'input-error' : ''}`}
                          disabled={loading}
                        />
                        {fieldErrors.workspaceJoinName && (
                          <div className="field-error-message">{fieldErrors.workspaceJoinName}</div>
                        )}
                      </div>
                    </>
                  )}
                  <div className="form-group">
                    <label className="form-label" htmlFor="companyLoginEmail">
                      {companyLoginMode === 'join' ? 'Work email' : 'Company email'}
                    </label>
                    <input
                      id="companyLoginEmail"
                      type="email"
                      placeholder={companyLoginMode === 'join' ? 'Enter work email' : 'Company Email ID'}
                      value={companyLoginEmail}
                      onChange={(e) => {
                        setCompanyLoginEmail(e.target.value);
                        setError('');
                      }}
                      className={`form-input ${fieldErrors.companyLoginEmail ? 'input-error' : ''}`}
                      disabled={loading}
                    />
                    {fieldErrors.companyLoginEmail && <div className="field-error-message">{fieldErrors.companyLoginEmail}</div>}
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="companyLoginPassword">Password</label>
                    <div className="password-input-wrap">
                      <input
                        id="companyLoginPassword"
                        type={visiblePasswords.companyLoginPassword ? 'text' : 'password'}
                        placeholder={companyLoginMode === 'join' ? 'Create password' : 'Password'}
                        value={companyLoginPassword}
                        onChange={(e) => {
                          setCompanyLoginPassword(e.target.value);
                          setError('');
                        }}
                        autoComplete="current-password"
                        className={`form-input has-password-toggle ${fieldErrors.companyLoginPassword ? 'input-error' : ''}`}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => togglePasswordVisibility('companyLoginPassword')}
                        disabled={loading}
                        aria-label={visiblePasswords.companyLoginPassword ? 'Hide password' : 'Show password'}
                        tabIndex={-1}
                      >
                        {visiblePasswords.companyLoginPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {fieldErrors.companyLoginPassword && <div className="field-error-message">{fieldErrors.companyLoginPassword}</div>}
                  </div>

                  {companyLoginMode === 'join' && (
                    <div className="form-group">
                      <label className="form-label" htmlFor="workspaceJoinConfirmPassword">Confirm password</label>
                      <div className="password-input-wrap">
                        <input
                          id="workspaceJoinConfirmPassword"
                          type={visiblePasswords.workspaceJoinConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirm password"
                          value={workspaceJoinConfirmPassword}
                          onChange={(e) => {
                            setWorkspaceJoinConfirmPassword(e.target.value);
                            setError('');
                          }}
                          autoComplete="new-password"
                          className={`form-input has-password-toggle ${fieldErrors.workspaceJoinConfirmPassword ? 'input-error' : ''}`}
                          disabled={loading}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => togglePasswordVisibility('workspaceJoinConfirmPassword')}
                          disabled={loading}
                          aria-label={visiblePasswords.workspaceJoinConfirmPassword ? 'Hide password' : 'Show password'}
                          tabIndex={-1}
                        >
                          {visiblePasswords.workspaceJoinConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      {fieldErrors.workspaceJoinConfirmPassword && (
                        <div className="field-error-message">{fieldErrors.workspaceJoinConfirmPassword}</div>
                      )}
                    </div>
                  )}

                  <button type="submit" className="btn-verify" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-small"></span>
                        <span>{companyLoginMode === 'join' ? 'Joining...' : 'Signing in...'}</span>
                      </>
                    ) : companyLoginMode === 'join' ? (
                      'Join workspace'
                    ) : (
                      'Login'
                    )}
                  </button>

                  {companyLoginMode === 'join' ? (
                    <button
                      type="button"
                      className="btn-change-email-link btn-change-email-link-top"
                      onClick={() => {
                        setCompanyLoginMode('login');
                        setError('');
                      }}
                      disabled={loading}
                    >
                      Have a password? Company login
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn-change-email-link btn-change-email-link-top"
                        onClick={switchToCompanyRegister}
                        disabled={loading}
                      >
                        Create New Company Account
                      </button>
                      <button
                        type="button"
                        className="btn-change-email-link btn-change-email-link-bottom"
                        onClick={switchToOtpLogin}
                        disabled={loading}
                      >
                        Switch to Login
                      </button>
                    </>
                  )}
                </form>
              )}

              {authMode === 'company-register' && (
                <form onSubmit={handleCompanyRegister} className="login-form">
                  <div className="company-section-card">
                    <h4>Create New Company Account</h4>
                    <p>Register your company and access your dashboard</p>
                  </div>

                  <div className="form-group">
                    <input
                      type="text"
                      placeholder="Company Name"
                      value={companyName}
                      onChange={(e) => {
                        setCompanyName(e.target.value);
                        setError('');
                      }}
                      className={`form-input ${fieldErrors.companyName ? 'input-error' : ''}`}
                      disabled={loading}
                    />
                    {fieldErrors.companyName && <div className="field-error-message">{fieldErrors.companyName}</div>}
                  </div>

                  <div className="form-group">
                    <input
                      type="email"
                      placeholder="Email ID"
                      value={companyEmail}
                      onChange={(e) => {
                        setCompanyEmail(e.target.value);
                        setError('');
                      }}
                      className={`form-input ${fieldErrors.companyEmail ? 'input-error' : ''}`}
                      disabled={loading}
                    />
                    {fieldErrors.companyEmail && <div className="field-error-message">{fieldErrors.companyEmail}</div>}
                  </div>

                  <div className="form-group">
                    <input
                      type="tel"
                      placeholder="Contact Number (for OTP)"
                      value={companyContactNumber}
                      onChange={(e) => {
                        setCompanyContactNumber(e.target.value);
                        setError('');
                        resetAadhaarVerification();
                      }}
                      className={`form-input ${fieldErrors.companyContactNumber ? 'input-error' : ''}`}
                      disabled={loading || aadhaarVerified}
                    />
                    {fieldErrors.companyContactNumber && <div className="field-error-message">{fieldErrors.companyContactNumber}</div>}
                  </div>

                  <div className={`aadhaar-verify-card ${aadhaarVerified ? 'aadhaar-verify-card--done' : ''}`}>
                    <div className="aadhaar-verify-head">
                      <h4>Aadhaar Verification</h4>
                      {aadhaarVerified ? (
                        <span className="aadhaar-verified-badge">
                          <FaCheckCircle /> Verified
                        </span>
                      ) : null}
                    </div>
                    <p className="aadhaar-verify-hint">
                      Enter your Aadhaar, then tap Send OTP. OTP goes to your contact number and company email
                      {aadhaarConfig.configured ? ' (Aadhaar KYC when available).' : '.'}
                    </p>

                    {aadhaarVerified && aadhaarVerifiedPhoto && (
                      <div className="aadhaar-verified-profile">
                        <img
                          src={aadhaarVerifiedPhoto}
                          alt="Aadhaar verified photo"
                          className="aadhaar-verified-photo"
                        />
                        <div className="aadhaar-verified-profile-info">
                          {aadhaarVerifiedName && (
                            <span className="aadhaar-verified-name">{aadhaarVerifiedName}</span>
                          )}
                          <span className="aadhaar-verified-caption">Photo fetched from Aadhaar KYC</span>
                        </div>
                      </div>
                    )}

                    <div className="form-group">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Aadhaar Number (12 digits)"
                        value={companyAadhaar}
                        onChange={(e) => {
                          setCompanyAadhaar(formatAadhaarInput(e.target.value));
                          setError('');
                          resetAadhaarVerification();
                        }}
                        onBlur={() => {
                          const digits = getAadhaarDigits(companyAadhaar);
                          if (
                            digits.length === 12 &&
                            !aadhaarOtpSent &&
                            !aadhaarVerified &&
                            !aadhaarOtpSending &&
                            (!aadhaarConfig.requiresContactForOtp ||
                              !validatePhone(companyContactNumber, 'Contact number'))
                          ) {
                            handleSendAadhaarOtp();
                          }
                        }}
                        className={`form-input ${fieldErrors.companyAadhaar ? 'input-error' : ''}`}
                        disabled={loading || aadhaarVerified}
                        maxLength={14}
                      />
                      {fieldErrors.companyAadhaar && !aadhaarVerified && (
                        <div className="field-error-message">{fieldErrors.companyAadhaar}</div>
                      )}
                    </div>

                    {!aadhaarVerified && (
                      <>
                        <button
                          type="button"
                          className="btn-aadhaar-send"
                          onClick={handleSendAadhaarOtp}
                          disabled={
                            loading ||
                            aadhaarOtpSending ||
                            getAadhaarDigits(companyAadhaar).length !== 12 ||
                            (aadhaarConfig.requiresContactForOtp &&
                              Boolean(validatePhone(companyContactNumber, 'Contact number')))
                          }
                        >
                          {aadhaarOtpSending ? (
                            <>
                              <span className="spinner-small"></span>
                              <span>Sending OTP...</span>
                            </>
                          ) : aadhaarOtpSent ? (
                            'Resend OTP'
                          ) : (
                            'Send OTP to Aadhaar Registered Mobile'
                          )}
                        </button>

                        {aadhaarOtpSent && (
                          <div className="aadhaar-otp-row">
                            <div className="form-group aadhaar-otp-input-wrap">
                              <input
                                type="text"
                                inputMode="numeric"
                                placeholder="Enter 6-digit OTP"
                                value={aadhaarOtp}
                                onChange={(e) => {
                                  setAadhaarOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                                  setError('');
                                }}
                                className={`form-input ${fieldErrors.aadhaarOtp ? 'input-error' : ''}`}
                                disabled={loading || aadhaarVerifying}
                                maxLength={6}
                              />
                            </div>
                            <button
                              type="button"
                              className="btn-aadhaar-verify"
                              onClick={handleVerifyAadhaarOtp}
                              disabled={loading || aadhaarVerifying || aadhaarOtp.length !== 6}
                            >
                              {aadhaarVerifying ? 'Verifying...' : 'Verify'}
                            </button>
                          </div>
                        )}

                        {aadhaarMaskedMobile && aadhaarOtpSent && (
                          <p className="aadhaar-otp-sent-note">
                            OTP sent to {aadhaarMaskedMobile}
                          </p>
                        )}

                        {fieldErrors.aadhaarOtp && !aadhaarVerified && (
                          <div className="field-error-message">{fieldErrors.aadhaarOtp}</div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="form-group">
                    <textarea
                      placeholder="Registered Address"
                      value={companyAddress}
                      onChange={(e) => {
                        setCompanyAddress(e.target.value);
                        setError('');
                      }}
                      className={`form-input company-address-input ${fieldErrors.companyAddress ? 'input-error' : ''}`}
                      rows={3}
                      disabled={loading}
                    />
                    {fieldErrors.companyAddress && <div className="field-error-message">{fieldErrors.companyAddress}</div>}
                  </div>

                  <div className="form-group">
                    <div className="password-input-wrap">
                      <input
                        type={visiblePasswords.companyPassword ? 'text' : 'password'}
                        placeholder="Password"
                        value={companyPassword}
                        onChange={(e) => {
                          setCompanyPassword(e.target.value);
                          setError('');
                        }}
                        className={`form-input has-password-toggle ${fieldErrors.companyPassword ? 'input-error' : ''}`}
                        autoComplete="new-password"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => togglePasswordVisibility('companyPassword')}
                        disabled={loading}
                        aria-label={visiblePasswords.companyPassword ? 'Hide password' : 'Show password'}
                        tabIndex={-1}
                      >
                        {visiblePasswords.companyPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {fieldErrors.companyPassword && <div className="field-error-message">{fieldErrors.companyPassword}</div>}
                  </div>

                  <div className="form-group">
                    <div className="password-input-wrap">
                      <input
                        type={visiblePasswords.companyConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm Password"
                        value={companyConfirmPassword}
                        onChange={(e) => {
                          setCompanyConfirmPassword(e.target.value);
                          setError('');
                        }}
                        className={`form-input has-password-toggle ${fieldErrors.companyConfirmPassword ? 'input-error' : ''}`}
                        autoComplete="new-password"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => togglePasswordVisibility('companyConfirmPassword')}
                        disabled={loading}
                        aria-label={visiblePasswords.companyConfirmPassword ? 'Hide password' : 'Show password'}
                        tabIndex={-1}
                      >
                        {visiblePasswords.companyConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {fieldErrors.companyConfirmPassword && (
                      <div className="field-error-message">{fieldErrors.companyConfirmPassword}</div>
                    )}
                  </div>

                  <button type="submit" className="btn-verify" disabled={loading || !aadhaarVerified}>
                    {loading ? (
                      <>
                        <span className="spinner-small"></span>
                        <span>Creating account...</span>
                      </>
                    ) : (
                      'Create New Company Account'
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn-change-email-link btn-change-email-link-bottom"
                    onClick={switchToCompanyLogin}
                    disabled={loading}
                  >
                    Switch to Login
                  </button>
                </form>
              )}

              {authMode === 'otp' && step === 'email' && (
                <>
                  <div className="divider">
                    <span>or</span>
                  </div>
                  <button onClick={handleGoogleSignIn} className="btn-google" disabled={loading}>
                    <FaGoogle className="google-icon" />
                    <span>Continue with Google</span>
                  </button>
                </>
              )}
            </motion.div>

            {showSuccess && (
              <motion.div
                className="success-feedback"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
              >
                <FaCheckCircle className="success-icon" />
                <span>Login successful! Redirecting...</span>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LoginModal;
